/**
 * Synchronous token snapshot for the `ApiClient`'s region ROUTER.
 *
 * Region routing (region/continent host swap + minute failover) is decided
 * per-request and SYNCHRONOUSLY inside `ApiClient.resolveUrl`, but the session
 * (and therefore the decoded token carrying `region_config` / `continent`) only
 * becomes available asynchronously after Auth0 resolves. This tiny module
 * bridges the two: the session layer pushes the current decoded token here as
 * soon as it loads, and the router pulls it back synchronously.
 *
 * It is intentionally a module singleton (not React state) because the
 * `ApiClient` is created once at bootstrap, before React renders.
 *
 * This is a SNAPSHOT, taken once per page load, and it is not the credential.
 * The bearer comes from `SessionManager.getAccessToken()`, which re-mints an
 * expired token off the refresh token; authenticating from the `__raw` in here
 * is why a tab left open used to 403 on everything until it was reloaded, while
 * a more recently opened tab (holding a newer snapshot) carried on working.
 * `ApiClient` only falls back to `__raw` when there is no live token at all.
 */
import { routingTokenForBearer, type AutheToken } from "@authe/fe-commons/api";
import { appEnv } from "@/config";

let current: AutheToken | null = null;
let latestBearer: string | null = null;

/** Push the live session's decoded token (or `null` on sign-out). */
export function setRoutingToken(token: AutheToken | null): void {
  current = token;
}

/**
 * Record the access token a request is about to carry. Blue/green routing
 * follows it (see {@link getRoutingToken}), because the authorizer only lets a
 * bearer onto `/blue/*` — or a blue AppSync API — when THAT bearer routes the
 * service blue.
 */
export function noteBearer(token: string | null | undefined): void {
  if (token) latestBearer = token;
}

/** Wrap a token getter so every bearer it hands out is {@link noteBearer}-ed. */
export function trackBearer(getToken: () => Promise<string | null>): () => Promise<string | null> {
  return async () => {
    const token = await getToken();
    noteBearer(token);
    return token;
  };
}

/**
 * Current decoded token for region routing, or `null` before the session loads.
 *
 * Region claims come from the page-load snapshot; `qa_config` from the latest
 * bearer. A silent refresh after a tester's blue entry was removed mints a green
 * bearer, and routing REST, GraphQL and streaming by the stale snapshot sent it to
 * blue hosts the authorizer refuses — every call failed until a reload.
 */
export function getRoutingToken(): AutheToken | null {
  if (!current) return null;
  return routingTokenForBearer(current, latestBearer ?? current.__raw, appEnv.claimsNamespace);
}
